import { Stack, usePathname } from 'expo-router';
import { pendingReferral, referralCodeFromUrl } from '@/lib/pendingReferral';
import usePendingAffiliate from '@/hooks/usePendingAffiliate';
import * as SplashScreen from 'expo-splash-screen';
import { StatusBar } from 'expo-status-bar';
import { useEffect, useMemo } from 'react';
import { NativeModules, Platform, View, processColor } from 'react-native';
import { isDarkTheme } from '@/constants/Colors';
import 'react-native-reanimated';
import * as QuickActions from 'expo-quick-actions';
import { useQuickActionRouting } from 'expo-quick-actions/router';
import useKeepScreenAwake from '@/hooks/useKeepScreenAwake';
import { ThemeProvider, useTheme } from '@/context/ThemeContext';
import { GabPurchaseProvider } from '@/context/GabPurchaseContext';
import ErrorBoundary from '@/components/ErrorBoundary';
import MobileUpdateGate from '@/components/Updates/MobileUpdateGate';
import Text from '@/components/Typography/Text';
import { KeyboardProvider } from 'react-native-keyboard-controller';
import { TooltipProvider } from '@/components/Tooltip';
import { useLocaleBootstrap } from '@/hooks/useTranslation';
import { GestureHandlerRootView } from 'react-native-gesture-handler';
import { DropdownOverlayHost, DropdownBackground } from '@/components/DropdownMenu/DropdownMenu';
import PushNotificationsHost from '@/components/PushNotificationsHost';
import MobileLaunchActionsHost from '@/components/MobileLaunchActionsHost';
import CoworkerWatcher from '@/components/Coworker/CoworkerWatcher';
import MobileReviewHost from '@/components/Reviews/MobileReviewHost';
import { useMobileAiSharingAllowed } from '@/components/Auth/AiSharingConsentGate';
import { notifyReviewInteraction } from '@/lib/reviewActivity';
// import { API_HOST } from '@/constants/config'; // APM disabled on the frontend
// import { APP_VERSION } from '@/constants/config'; // APM disabled on the frontend
// Analytics helpers (ad click id persistence) removed 2026-09 with the
// Facebook and Reddit pixels.
import { setLandingPathDimension } from '@/constants/utils/analyticsEvents';
import { prefetchDeferredIcons } from '@/constants/iconRegistry';
import { getPublicRedirectTarget } from '@/lib/publicRedirects';
import { isKnownSpaRoute } from '@/lib/spaRoutes';
import { capturePendingPrompt } from '@/lib/pendingPrompt';
import { loadTurnstile } from '@/lib/turnstile';
// import { initApm } from '@/lib/apm'; // APM disabled on the frontend
import NotFoundRoute from './+not-found';
import SiteAnnouncementBanner from '@/components/SiteAnnouncementBanner';
import BrowserPanelBridge from '@/components/BrowserPanelBridge';
import { maintainMacWindowDimensions, updateMacWindowDimensions } from '@/lib/macWindowDimensions';

// CSS imports are web-only - uses platform-specific index.js/index.web.js
import './styles';

// Keep the splash screen visible while we fetch resources
SplashScreen.preventAutoHideAsync();

function RootLayoutContent() {
  usePendingAffiliate();
  const { colors, theme } = useTheme();
  const pathname = usePathname();
  const aiSharingAllowed = useMobileAiSharingAllowed();
  useLocaleBootstrap();
  useEffect(maintainMacWindowDimensions, []);

  useEffect(() => {
    if (Platform.OS === 'macos') {
      NativeModules.MacMenuBridge?.setWindowAppearance?.(isDarkTheme(theme), processColor(colors.solid_color_block));
    }
  }, [theme, colors.solid_color_block]);

  // Ad click id persistence (fbclid / rdt_cid) removed along with the
  // Facebook and Reddit pixels — 2026-09.

  // The entry page for this visit. Deliberately mount-only: a later in-app
  // navigation would overwrite the landing page with wherever the visitor
  // happens to be, which is what Matomo's page report already tells us.
  useEffect(() => {
    setLandingPathDimension();
  }, []);

  const publicRedirectTarget = useMemo(() => {
    if (Platform.OS !== 'web') return null;
    const path = pathname
      || (typeof window !== 'undefined' ? window.location.pathname : null);
    return path ? getPublicRedirectTarget(path) : null;
  }, [pathname]);

  useEffect(() => {
    if (Platform.OS !== 'web' || typeof window === 'undefined' || !publicRedirectTarget) return;
    const current = getPublicRedirectTarget(window.location.pathname);
    if (current) window.location.replace(current);
  }, [publicRedirectTarget]);

  const showNotFound = Platform.OS === 'web'
    && Boolean(pathname)
    && !publicRedirectTarget
    && !isKnownSpaRoute(pathname);
  useQuickActionRouting();

  // Keep native chats awake only when the user opts in through settings.
  useKeepScreenAwake();

  useEffect(() => {
    if (Platform.OS === 'ios' || Platform.OS === 'android') {
      QuickActions.setItems([
        {
          id: 'new-chat',
          title: 'New Chat',
          icon: Platform.OS === 'ios' ? 'symbol:plus.bubble' : 'shortcut_chat',
          params: { href: '/(app)/app' },
        },
      ]);
    }
  }, []);

  // Warm the deferred half of the icon map once the shell is up. Catalog icons are named
  // by database records, so they cannot be in the eager map; fetching the chunk on idle
  // means a later catalog page finds it already cached instead of showing empty boxes.
  useEffect(() => {
    prefetchDeferredIcons();
  }, []);

  useEffect(() => {
    // Small delay on native to allow auth store hydration from AsyncStorage
    // This prevents a flash of the landing page for authenticated users
    if (Platform.OS !== 'web') {
      const timer = setTimeout(() => SplashScreen.hideAsync(), 100);
      return () => clearTimeout(timer);
    }
    SplashScreen.hideAsync();
  }, []);

  // Capture referral code from ?ref=CODE query parameter (web only)
  useEffect(() => {
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      try {
        const params = new URLSearchParams(window.location.search);
        const refCode = referralCodeFromUrl(window.location.href);
        if (refCode) {
          pendingReferral.capture(refCode);
          // Strip the ref param from the URL without triggering a reload
          params.delete('ref');
          const newSearch = params.toString();
          const newUrl = window.location.pathname + (newSearch ? `?${newSearch}` : '') + window.location.hash;
          window.history.replaceState({}, '', newUrl);
        }
      } catch (e) {
        // Silently ignore — localStorage may be unavailable in some contexts
      }
    }
  }, []);

  // Capture inbound composer prompt from ?q= (web only).
  // Public SearchAction and sibling-property CTAs land here. Prefill only —
  // never auto-send, never include in signup/SSO bodies. Leave ?q= on the
  // URL so the homepage can redirect to /app?q=; /app strips it after apply.
  useEffect(() => {
    if (Platform.OS !== 'web' || typeof window === 'undefined') return;
    try {
      const q = new URLSearchParams(window.location.search).get('q');
      if (q) capturePendingPrompt(q);
    } catch (e) {
      // Silently ignore — localStorage may be unavailable in some contexts
    }
  }, []);

  // Capture checkout deep link params (web only)
  // URLs like /?checkout=plus&plan=yearly&promo=WELCOME20
  useEffect(() => {
    if (Platform.OS !== 'web' || typeof window === 'undefined') return;
    try {
      const params = new URLSearchParams(window.location.search);
      const checkoutType = params.get('checkout');
      if (!checkoutType) return;

      const pending = { type: checkoutType, ts: Date.now() };
      const plan = params.get('plan');
      const promo = params.get('promo');
      const pack = params.get('pack');
      if (plan) pending.plan = plan;
      if (promo) pending.promo = promo;
      if (pack) pending.pack = pack;

      localStorage.setItem('pendingCheckout', JSON.stringify(pending));

      params.delete('checkout');
      params.delete('plan');
      params.delete('promo');
      params.delete('pack');
      const newSearch = params.toString();
      const newUrl = window.location.pathname + (newSearch ? `?${newSearch}` : '') + window.location.hash;
      window.history.replaceState({}, '', newUrl);
    } catch (e) {
      // Silently ignore
    }
  }, []);

  useEffect(() => {
    if (Platform.OS !== 'web' || typeof window === 'undefined') return;
    if (!window.opener) return;

    const h = window.location.hash;
    if (!h || h.indexOf('id_token=') === -1) return;

    const params = {};
    h.substring(1).split('&').forEach(pair => {
      const idx = pair.indexOf('=');
      if (idx > 0) params[decodeURIComponent(pair.substring(0, idx))] = decodeURIComponent(pair.substring(idx + 1));
    });

    if (params.id_token) {
      window.opener.postMessage({ type: 'google-oauth-callback', id_token: params.id_token }, window.location.origin);
      window.close();
    }
  }, []);

  // Initialize APM (web-only for v1). Captures uncaught errors, promise
  // rejections, Web Vitals (LCP/FID/CLS), and fetch/XHR timings. Sends to
  // /json/apm/events with the user's JWT bearer token. Non-error events are
  // sampled at 10% in production, 100% in dev. Errors always bypass sampling.
  //
  // [APM disabled on the frontend]
  // useEffect(() => {
  //   if (Platform.OS !== 'web' || typeof window === 'undefined') return;
  //   initApm({
  //     endpoint: `${API_HOST}/json/apm/events`,
  //     release: `web:${APP_VERSION}`,
  //     sampleRate: __DEV__ ? 1.0 : 0.1,
  //     autoCapture: { errors: true, vitals: true, console: false, fetch: true },
  //     // Lazily read the JWT from the auth store on each flush (avoids stale
  //     // token capture + matches the pattern in lib/api.js).
  //     getAuthToken: () => {
  //       try {
  //         return require('@/store').useAuthStore.getState().token || null;
  //       } catch (_) {
  //         return null;
  //       }
  //     },
  //   });
  // }, []);

  // Load Turnstile script
  useEffect(() => {
    if (Platform.OS === 'web') {
      // Forms surface failures and can retry this preload on submission.
      loadTurnstile().catch(() => {});
    }
  }, []);

  if (publicRedirectTarget) {
    return null;
  }

  return (
    <GestureHandlerRootView style={{ flex: 1 }} onLayout={Platform.OS === 'macos' ? updateMacWindowDimensions : undefined}>
      <KeyboardProvider>
        <GabPurchaseProvider>
          <TooltipProvider>
            <View style={{ flex: 1, backgroundColor: colors.solid_color_primary }} onTouchStart={Platform.OS === 'ios' ? notifyReviewInteraction : undefined}>
                <BrowserPanelBridge />
                {Platform.OS === 'web' && <SiteAnnouncementBanner />}
                <DropdownBackground>{showNotFound ? (
                  <NotFoundRoute />
                ) : (
                <Stack screenOptions={{ 
                  headerShown: false,
                  contentStyle: { backgroundColor: colors.solid_color_primary },
                  headerBackVisible: false
                }}>
                  <Stack.Screen name="(app)" options={({ route }) => ({ animation: Platform.OS === 'ios' && ((route?.params && route.params.transition === 'fade_from_bottom') || (route?.state && route.state.routes?.[route.state.index || 0]?.params?.transition === 'fade_from_bottom')) ? 'fade_from_bottom' : undefined })} />
                  <Stack.Screen name="(auth)" options={({ route }) => ({ animation: Platform.OS === 'ios' && ((route?.params && route.params.transition === 'fade_from_bottom') || (route?.state && route.state.routes?.[route.state.index || 0]?.params?.transition === 'fade_from_bottom')) ? 'fade_from_bottom' : undefined })} />
                  <Stack.Screen name="(public)" options={{ animation: Platform.OS === 'ios' ? 'slide_from_right' : undefined }} />
                  <Stack.Screen name="+not-found" />
                </Stack>
                )}</DropdownBackground>
                {Platform.OS !== 'macos' && <StatusBar animated={true} style={colors.status_bar_style} />}
                {['ios', 'android'].includes(Platform.OS) && <PushNotificationsHost />}
                {aiSharingAllowed && <MobileReviewHost />}
                {aiSharingAllowed && <MobileLaunchActionsHost />}
                {Platform.OS === 'ios' && aiSharingAllowed && <CoworkerWatcher />}
                {DropdownOverlayHost && <DropdownOverlayHost />}
              </View>
          </TooltipProvider>
        </GabPurchaseProvider>
      </KeyboardProvider>
    </GestureHandlerRootView>
  );
}

export default function RootLayout() {
  return (
    <ErrorBoundary>
      <MobileUpdateGate>
        <ThemeProvider>
          <RootLayoutContent />
        </ThemeProvider>
      </MobileUpdateGate>
    </ErrorBoundary>
  );
}
